// ─────────────────────────────────────────────────────────────
// HomeView — default landing view.
// Jamie hero dominates the top; three secondary action cards sit
// below in a responsive grid.
// ─────────────────────────────────────────────────────────────

import { Box, Button, Paper, Typography, Avatar } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import BugReportOutlinedIcon from "@mui/icons-material/BugReportOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import type { ViewId } from "../components/AppShell";

// ── Types ──────────────────────────────────────────────────────

export interface HomeViewProps {
  onNavigate: (view: ViewId) => void;
}

interface SecondaryCard {
  title: string;
  description: string;
  icon: React.ReactNode;
  accent: string;
  targetView: ViewId;
  ariaLabel: string;
}

// ── Card data ──────────────────────────────────────────────────

const SECONDARY_CARDS: SecondaryCard[] = [
  {
    title: "Suggest a Feature",
    description:
      "Have an idea for a new capability? Tell Jamie or add it directly to the backlog.",
    icon: <LightbulbOutlinedIcon />,
    accent: "#4caf50",
    targetView: "backlog",
    ariaLabel: "Suggest a feature — navigate to backlog",
  },
  {
    title: "View the Sprint Log",
    description:
      "See completed sprints and everything that's shipped so far.",
    icon: <HistoryOutlinedIcon />,
    accent: "#2196f3",
    targetView: "history",
    ariaLabel: "View the sprint log — navigate to history",
  },
  {
    title: "Report a Bug",
    description:
      "Something broken? Log it here so it gets tracked and fixed.",
    icon: <BugReportOutlinedIcon />,
    accent: "#f44336",
    targetView: "backlog",
    ariaLabel: "Report a bug — navigate to backlog",
  },
  {
    title: "View the Backlog",
    description:
      "Browse all feature requests and bug reports. See what's queued, in progress, and done.",
    icon: <ListAltOutlinedIcon />,
    accent: "#ff9800",
    targetView: "backlog",
    ariaLabel: "View the backlog",
  },
];

// ── Sub-components ─────────────────────────────────────────────

interface ActionCardProps {
  card: SecondaryCard;
  onClick: () => void;
  sx?: SxProps<Theme>;
}

function ActionCard({ card, onClick, sx }: ActionCardProps) {
  return (
    <Paper
      variant="outlined"
      onClick={onClick}
      tabIndex={0}
      role="button"
      aria-label={card.ariaLabel}
      onKeyDown={(e) => e.key === "Enter" && onClick()}
      sx={[
        {
          p: 2.5,
          cursor: "pointer",
          display: "flex",
          gap: 2,
          alignItems: "flex-start",
          borderRadius: 2,
          "&:hover": { backgroundColor: "action.hover" },
          "&:focus-visible": {
            outline: "2px solid",
            outlineColor: "primary.main",
            outlineOffset: "2px",
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {/* Left accent bar */}
      <Box
        aria-hidden
        sx={{
          width: 4,
          borderRadius: 1,
          alignSelf: "stretch",
          backgroundColor: card.accent,
          flexShrink: 0,
        }}
      />

      {/* Icon + text */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: card.accent }}>
          {card.icon}
          <Typography variant="subtitle2" component="h3" sx={{ fontWeight: 600 }}>
            {card.title}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {card.description}
        </Typography>
      </Box>
    </Paper>
  );
}

// ── HomeView ───────────────────────────────────────────────────

export function HomeView({ onNavigate }: HomeViewProps) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "auto",
        px: 4,
        py: 4,
        gap: 3,
      }}
    >
      {/* Jamie hero */}
      <Paper
        variant="outlined"
        sx={{
          borderRadius: 3,
          p: 5,
          textAlign: "center",
          background:
            "linear-gradient(135deg, rgba(156,39,176,0.08) 0%, transparent 60%)",
        }}
      >
        <Avatar
          src={`${import.meta.env.BASE_URL}avatars/jamie.png`}
          alt="Jamie"
          sx={{ width: 96, height: 96, mx: "auto", mb: 2, border: "3px solid", borderColor: "#9c27b0" }}
        />

        <Typography variant="h2" component="h1">
          Meet Jamie, your AI Product Owner
        </Typography>

        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ maxWidth: 540, mx: "auto", mt: 1 }}
        >
          Jamie is here to help shape the Maps Discover Studio product. Tell her
          about a feature you'd like to see, report something that's broken, or
          just ask what's being worked on — she'll make sure nothing falls
          through the cracks.
        </Typography>

        <Button
          variant="contained"
          size="large"
          sx={{ mt: 3, px: 4 }}
          onClick={() => onNavigate("jamie")}
        >
          Chat with Jamie
        </Button>
      </Paper>

      {/* Three secondary action cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2,
        }}
      >
        {SECONDARY_CARDS.map((card) => (
          <ActionCard
            key={card.title}
            card={card}
            onClick={() => onNavigate(card.targetView)}
          />
        ))}
      </Box>
    </Box>
  );
}
